import {Component, computed, inject, signal, WritableSignal} from '@angular/core';
import {AuthService} from '@services/auth.service';
import {FormsModule} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {UserStateService} from '@services/frontend/user-state.service';
import {AuthLoginResponse} from '@models/auth.interface';
import {NotificationService} from '@services/frontend/notification.service';
// import {SubscriptionService} from '@services/subscription.service';
import {Role} from '@models/user.interface';
import {HttpErrorResponse} from '@angular/common/http';

export type LoginView = 'login' | 'forgotPassword' | 'forgotIdentifier' | 'emailNotVerified';

@Component({
  selector: 'app-login',
  imports: [FormsModule, RouterLink],
  templateUrl: './login.html'
})
export class Login {

  private notificationService = inject(NotificationService);
  private authService = inject(AuthService);
  private userStateService = inject(UserStateService);
  // private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);

  email: WritableSignal<string> = signal('');
  password: WritableSignal<string> = signal('');
  view: WritableSignal<LoginView> = signal('login');
  loading: WritableSignal<boolean> = signal(false);

  errorMessage: WritableSignal<string> = signal('');
  resendingVerification: WritableSignal<boolean> = signal(false);

  isLoginFormValid = computed(() =>
    this.email().trim().length > 0 &&
    this.password().trim().length > 0
  );

  isRecoveryFormValid = computed(() => this.email().trim().length > 0);

  setView(next: LoginView): void {
    this.view.set(next);
    this.errorMessage.set('');
  }

  login(): void {
    if (!this.isLoginFormValid()) {
      return;
    }
    this.errorMessage.set('');
    this.loading.set(true);
    this.authService.login(this.email(), this.password()).subscribe({
      next: (auth: AuthLoginResponse) => {
        this.loading.set(false);
        this.userStateService.setToken(auth.token);
        this.notificationService.success('Authentification réussie !', 2000);
        this.handlePendingCheckoutOrNavigate();
      },
      error: (error: HttpErrorResponse) => {
        this.loading.set(false);
        const body = error.error;
        if (error.status === 403 && typeof body === 'string' && body.includes('EMAIL_NOT_VERIFIED')) {
          this.view.set('emailNotVerified');
          return;
        }
        const msg = typeof body === 'string' && body.length > 0
          ? body
          : 'Le mail ou le mot de passe est incorrect.';
        this.errorMessage.set(msg);
      }
    });
  }

  submitRecovery(): void {
    if (!this.isRecoveryFormValid()) {
      return;
    }
    this.loading.set(true);
    this.errorMessage.set('');
    const req = this.view() === 'forgotPassword'
      ? this.authService.forgotPassword(this.email())
      : this.authService.forgotIdentifier(this.email());

    req.subscribe({
      next: (res) => {
        this.loading.set(false);
        this.notificationService.success(res.message, 6000);
        this.setView('login');
      },
      error: () => {
        this.loading.set(false);
        this.notificationService.success(
          'Si cette adresse est reconnue, vous recevrez un message sous peu.',
          6000
        );
        this.setView('login');
      }
    });
  }

  resendVerification(): void {
    this.resendingVerification.set(true);
    this.authService.resendVerification(this.email()).subscribe({
      next: () => {
        this.resendingVerification.set(false);
        this.notificationService.success('Un nouveau lien de vérification a été envoyé à votre adresse e-mail.', 5000);
      },
      error: () => {
        this.resendingVerification.set(false);
        this.notificationService.success('Si cette adresse est reconnue, un nouveau lien vous sera envoyé.', 5000);
      },
    });
  }

  private handlePendingCheckoutOrNavigate(): void {
    // const pendingPrice = sessionStorage.getItem('pending_checkout_price');
    // if (pendingPrice) {
    //   sessionStorage.removeItem('pending_checkout_price');
    //   this.subscriptionService.createCheckout(pendingPrice).subscribe({ ... });
    // } else {
    void this.router.navigate(['/dashboard']);
    // }
  }

  protected readonly Role = Role;
}
